import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import '../styles/AdminDashboard.css';

interface AdminStats {
  profiles: { total: number };
  admins: { total: number };
  books: Record<string, { total: number; today: number }>;
  book_rate_limit: {
    global_window_today: number;
    global_per_day: number;
    circuit_open: boolean;
    _note: string;
  };
  reading_analytics?: Record<string, {
    sessions: number;
    unique_readers: number;
    total_pages_viewed: number;
    avg_pages_per_session: number;
    avg_duration_s: number;
  }>;
}

interface AdminEntry {
  added_by: string;
  added_at?: string;
  break_glass?: boolean;
}

interface AdminUsersData {
  admins: Record<string, AdminEntry>;
}

// Career types for admin editor
interface JobFields {
  id: string; title: string; location: string; type: string;
  postedDate: string; description: string;
  responsibilities: string[]; qualifications: string[];
  skills: string[]; benefits: string[];
}
interface AdminJobMeta {
  id: string; title: string; location: string; type: string;
  postedDate: string; description: string;
  locales: Record<string, JobFields>;
  hasLocales: string[];
}

type Tab = 'inbox' | 'stats' | 'users' | 'careers';

interface CommunicationResponse {
  body: string;
  admin_name: string;
  admin_uid?: string;
  responded_at: string;
}

type CommunicationSource = 'contact' | 'career' | 'booking' | 'dhanurveda_booking' | 'book_feedback';
type CommunicationStatus = 'open' | 'in_progress' | 'resolved';

interface AdminCommunication {
  id: string;
  source: CommunicationSource;
  source_id: string;
  subject: string;
  sender: {
    name: string;
    email: string;
    phone: string;
    uid: string;
  };
  message: string;
  created_at: string;
  details: Record<string, string | number | boolean>;
  status: CommunicationStatus;
  assignee_uid: string | null;
  assigned_at: string | null;
  assigned_by: string | null;
  response: CommunicationResponse | null;
}

interface CommunicationAdmin {
  uid: string;
  display_name: string;
}

interface CommunicationsData {
  items: AdminCommunication[];
  admins: CommunicationAdmin[];
  counts: {
    open: number;
    unassigned: number;
    mine: number;
  };
}

const COMMUNICATION_SOURCE_LABELS: Record<CommunicationSource, string> = {
  contact: 'Contact',
  career: 'Career',
  booking: 'Booking',
  dhanurveda_booking: 'Dhanurveda booking',
  book_feedback: 'Book feedback',
};

export default function AdminDashboard({ onNavigate }: { onNavigate?: (p: string) => void }) {
  const { user, isAdmin, adminLoading } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('inbox');
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [adminUsers, setAdminUsers] = useState<AdminUsersData | null>(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [newUid, setNewUid] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [addSuccess, setAddSuccess] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);

  // ── Careers state ──────────────────────────────────────────────────────────
  const BLANK_FIELDS: JobFields = {
    id: '', title: '', location: '', type: 'अनुबंध (६ मास, नवीकरणीय)',
    postedDate: new Date().toISOString().slice(0, 10),
    description: '', responsibilities: [], qualifications: [], skills: [], benefits: [],
  };
  const [careers, setCareers] = useState<AdminJobMeta[]>([]);
  const [supportedLocales, setSupportedLocales] = useState<string[]>(['hi', 'en']);
  const [careersLoading, setCareersLoading] = useState(false);
  const [careersError, setCareersError] = useState<string | null>(null);
  // editingJobId = null means "new job"; undefined means form closed
  const [editingJobId, setEditingJobId] = useState<string | null | undefined>(undefined);
  const [formLocale, setFormLocale] = useState<string>('hi');
  const [jobDraft, setJobDraft] = useState<JobFields>(BLANK_FIELDS);
  const [jobSaveError, setJobSaveError] = useState<string | null>(null);
  const [jobSaving, setJobSaving] = useState(false);

  const [communications, setCommunications] = useState<AdminCommunication[]>([]);
  const [communicationAdmins, setCommunicationAdmins] = useState<CommunicationAdmin[]>([]);
  const [communicationCounts, setCommunicationCounts] = useState({ open: 0, unassigned: 0, mine: 0 });
  const [communicationsLoading, setCommunicationsLoading] = useState(false);
  const [communicationsError, setCommunicationsError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'resolved'>('open');
  const [assignmentFilter, setAssignmentFilter] = useState<'all' | 'unassigned' | 'mine'>('all');
  const [sourceFilter, setSourceFilter] = useState<'all' | CommunicationSource>('all');
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({});
  const [updatingCommunicationId, setUpdatingCommunicationId] = useState<string | null>(null);

  const authFetch = useCallback(
    async (url: string, options: RequestInit = {}) => {
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken(/* forceRefresh */ true);
      const resp = await fetch(url, {
        ...options,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...(options.headers ?? {}),
        },
      });
      if (!resp.ok) {
        const detail = await resp.json().catch(() => ({ detail: resp.statusText }));
        throw new Error(detail.error ?? detail.detail ?? `HTTP ${resp.status}`);
      }
      return resp.json();
    },
    [user],
  );

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    setStatsError(null);
    try {
      const data = await authFetch('/api/admin/stats');
      setStats(data);
    } catch (e) {
      setStatsError((e as Error).message);
    } finally {
      setStatsLoading(false);
    }
  }, [authFetch]);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const data = await authFetch('/api/admin/users');
      setAdminUsers(data);
    } catch (e) {
      setUsersError((e as Error).message);
    } finally {
      setUsersLoading(false);
    }
  }, [authFetch]);

  const loadCareers = useCallback(async () => {
    setCareersLoading(true);
    setCareersError(null);
    try {
      const data = await authFetch('/api/admin/careers');
      setCareers(data.jobs ?? []);
      if (data.supportedLocales) setSupportedLocales(data.supportedLocales);
    } catch (e) {
      setCareersError((e as Error).message);
    } finally {
      setCareersLoading(false);
    }
  }, [authFetch]);

  const loadCommunications = useCallback(async () => {
    setCommunicationsLoading(true);
    setCommunicationsError(null);
    try {
      const data = await authFetch('/api/admin/communications') as CommunicationsData;
      const items = data.items ?? [];
      setCommunications(items);
      setCommunicationAdmins(data.admins ?? []);
      setCommunicationCounts(data.counts ?? { open: 0, unassigned: 0, mine: 0 });
      setReplyDrafts((prev) => {
        const next = { ...prev };
        for (const item of items) {
          if (next[item.id] === undefined) next[item.id] = item.response?.body ?? '';
        }
        return next;
      });
    } catch (e) {
      setCommunicationsError((e as Error).message);
    } finally {
      setCommunicationsLoading(false);
    }
  }, [authFetch]);

  const assignCommunication = async (item: AdminCommunication, assigneeUid: string) => {
    setUpdatingCommunicationId(item.id);
    setCommunicationsError(null);
    try {
      await authFetch(`/api/admin/communications/${encodeURIComponent(item.id)}/assignment`, {
        method: 'PATCH',
        body: JSON.stringify({
          assignee_uid: assigneeUid || null,
          expected_assignee_uid: item.assignee_uid,
        }),
      });
      await loadCommunications();
    } catch (e) {
      await loadCommunications();
      setCommunicationsError((e as Error).message);
    } finally {
      setUpdatingCommunicationId(null);
    }
  };

  const updateCommunicationStatus = async (item: AdminCommunication, status: 'open' | 'resolved') => {
    setUpdatingCommunicationId(item.id);
    setCommunicationsError(null);
    try {
      await authFetch(`/api/admin/communications/${encodeURIComponent(item.id)}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      await loadCommunications();
    } catch (e) {
      setCommunicationsError((e as Error).message);
    } finally {
      setUpdatingCommunicationId(null);
    }
  };

  const sendReply = async (item: AdminCommunication) => {
    const text = (replyDrafts[item.id] ?? '').trim();
    if (!text) {
      setCommunicationsError('Write a response before sending.');
      return;
    }
    setUpdatingCommunicationId(item.id);
    setCommunicationsError(null);
    try {
      await authFetch(`/api/admin/book-feedback/${encodeURIComponent(item.source_id)}/respond`, {
        method: 'POST',
        body: JSON.stringify({ body: text }),
      });
      await loadCommunications();
    } catch (e) {
      setCommunicationsError((e as Error).message);
    } finally {
      setUpdatingCommunicationId(null);
    }
  };

  useEffect(() => {
    if (!isAdmin || adminLoading) return;
    if (activeTab === 'stats') loadStats();
    if (activeTab === 'users') loadUsers();
    if (activeTab === 'careers') loadCareers();
    if (activeTab === 'inbox') loadCommunications();
  }, [isAdmin, adminLoading, activeTab, loadStats, loadUsers, loadCareers, loadCommunications]);

  useEffect(() => {
    if (!isAdmin || adminLoading) return;
    loadCommunications();
  }, [isAdmin, adminLoading, loadCommunications]);

  // helpers: convert array ↔ newline-separated textarea text
  const arrToText = (arr: string[]) => arr.join('\n');
  const textToArr = (t: string) => t.split('\n').map(s => s.trim()).filter(Boolean);

  const loadLocaleIntoForm = (job: AdminJobMeta | null, locale: string) => {
    if (!job) {
      // new job
      setJobDraft({ ...BLANK_FIELDS });
      return;
    }
    const localeData = job.locales[locale];
    if (localeData) {
      setJobDraft({ ...localeData });
    } else {
      // locale not yet translated — start from blank but keep id
      setJobDraft({ ...BLANK_FIELDS, id: job.id });
    }
  };

  const openNewJob = () => {
    setEditingJobId(null);
    setFormLocale('hi');
    setJobDraft({ ...BLANK_FIELDS });
    setJobSaveError(null);
  };

  const openEditJob = (job: AdminJobMeta) => {
    setEditingJobId(job.id);
    setFormLocale('hi');
    loadLocaleIntoForm(job, 'hi');
    setJobSaveError(null);
  };

  const handleFormLocaleSwitch = (newLocale: string) => {
    // find current job meta (if editing)
    const jobMeta = editingJobId != null
      ? careers.find(j => j.id === editingJobId) ?? null
      : null;
    setFormLocale(newLocale);
    loadLocaleIntoForm(jobMeta, newLocale);
    setJobSaveError(null);
  };

  const handleJobSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setJobSaving(true);
    setJobSaveError(null);
    const isNew = editingJobId === null;
    const method = isNew ? 'POST' : 'PUT';
    const url = isNew
      ? `/api/admin/careers?locale=${encodeURIComponent(formLocale)}`
      : `/api/admin/careers/${encodeURIComponent(editingJobId!)}?locale=${encodeURIComponent(formLocale)}`;
    try {
      await authFetch(url, { method, body: JSON.stringify(jobDraft) });
      await loadCareers();
      if (isNew) {
        // After creating, switch to editing so user can fill other locales
        setEditingJobId(jobDraft.id || jobDraft.title);
      }
      setJobSaveError(null);
    } catch (e) {
      setJobSaveError((e as Error).message);
    } finally {
      setJobSaving(false);
    }
  };

  const handleJobDelete = async (jobId: string) => {
    if (!confirm(`Delete "${jobId}" from ALL locales? This cannot be undone.`)) return;
    try {
      await authFetch(`/api/admin/careers/${encodeURIComponent(jobId)}`, { method: 'DELETE' });
      if (editingJobId === jobId) setEditingJobId(undefined);
      await loadCareers();
    } catch (e) {
      setCareersError((e as Error).message);
    }
  };

  const handleAddAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddError(null);
    setAddSuccess(null);
    const uid = newUid.trim();
    if (!uid) return;
    try {
      const data = await authFetch('/api/admin/users', {
        method: 'POST',
        body: JSON.stringify({ uid }),
      });
      setAddSuccess(
        data.status === 'already_admin'
          ? `${uid} is already an admin.`
          : `Admin added: ${uid}`,
      );
      setNewUid('');
      loadUsers();
    } catch (e) {
      setAddError((e as Error).message);
    }
  };

  const handleRemoveAdmin = async (uid: string) => {
    if (!confirm(`Remove admin access for ${uid}?`)) return;
    setRemoving(uid);
    try {
      await authFetch(`/api/admin/users/${encodeURIComponent(uid)}`, { method: 'DELETE' });
      loadUsers();
    } catch (e) {
      setUsersError((e as Error).message);
    } finally {
      setRemoving(null);
    }
  };

  const visibleCommunications = communications.filter((item) => {
    const statusMatches = statusFilter === 'all'
      || (statusFilter === 'open' ? item.status !== 'resolved' : item.status === 'resolved');
    const assignmentMatches = assignmentFilter === 'all'
      || (assignmentFilter === 'unassigned'
        ? !item.assignee_uid
        : item.assignee_uid === user?.uid);
    return statusMatches
      && assignmentMatches
      && (sourceFilter === 'all' || item.source === sourceFilter);
  });

  const adminName = (uid: string | null) => {
    if (!uid) return 'Unassigned';
    const admin = communicationAdmins.find((entry) => entry.uid === uid);
    return admin?.display_name || uid;
  };

  const emailReplyHref = (item: AdminCommunication) => {
    const subject = `Re: ${item.subject} [${item.source_id}]`;
    return `mailto:${encodeURIComponent(item.sender.email)}?subject=${encodeURIComponent(subject)}`;
  };

  if (adminLoading) {
    return (
      <div className="admin-page admin-loading">
        <span className="admin-spinner" aria-hidden="true" />
        <p>Checking admin access…</p>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return (
      <div className="admin-page admin-denied">
        <div className="admin-denied-card">
          <div className="admin-denied-icon">🔒</div>
          <h1>Access Restricted</h1>
          <p>This control panel requires administrator access.</p>
          {!user && (
            <button className="admin-btn admin-btn-primary" onClick={() => onNavigate?.('/profile')}>
              Sign In
            </button>
          )}
          <button className="admin-btn admin-btn-ghost" onClick={() => onNavigate?.('/')}>
            ← Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <header className="admin-header">
        <div className="admin-header-left">
          <button className="admin-back-btn" onClick={() => onNavigate?.('/')}>
            ← Home
          </button>
          <div>
            <h1>⚙ Admin Control Panel</h1>
            <p className="admin-uid-label">
              Signed in as <code>{user.uid}</code>
            </p>
          </div>
        </div>
      </header>

      <nav className="admin-tabs" role="tablist">
        <button
          role="tab"
          className={`admin-tab ${activeTab === 'inbox' ? 'active' : ''}`}
          onClick={() => setActiveTab('inbox')}
          aria-selected={activeTab === 'inbox'}
        >
          📥 Inbox
          {communicationCounts.open > 0 && (
            <span className="admin-tab-count">{communicationCounts.open}</span>
          )}
        </button>
        <button
          role="tab"
          className={`admin-tab ${activeTab === 'stats' ? 'active' : ''}`}
          onClick={() => setActiveTab('stats')}
          aria-selected={activeTab === 'stats'}
        >
          📊 Stats
        </button>
        <button
          role="tab"
          className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
          aria-selected={activeTab === 'users'}
        >
          👤 Admin Users
        </button>
        <button
          role="tab"
          className={`admin-tab ${activeTab === 'careers' ? 'active' : ''}`}
          onClick={() => setActiveTab('careers')}
          aria-selected={activeTab === 'careers'}
        >
          💼 Careers
        </button>
      </nav>

      <div className="admin-content" role="tabpanel">
        {activeTab === 'inbox' && (
          <>
            <div className="admin-inbox-summary">
              <button
                type="button"
                className={`admin-card admin-inbox-stat ${assignmentFilter === 'unassigned' ? 'active' : ''}`}
                onClick={() => setAssignmentFilter('unassigned')}
              >
                <span className="admin-card-value">{communicationCounts.unassigned}</span>
                <span className="admin-card-label">Unassigned</span>
              </button>
              <button
                type="button"
                className={`admin-card admin-inbox-stat ${assignmentFilter === 'mine' ? 'active' : ''}`}
                onClick={() => setAssignmentFilter('mine')}
              >
                <span className="admin-card-value">{communicationCounts.mine}</span>
                <span className="admin-card-label">Assigned to me</span>
              </button>
              <button
                type="button"
                className={`admin-card admin-inbox-stat ${assignmentFilter === 'all' ? 'active' : ''}`}
                onClick={() => setAssignmentFilter('all')}
              >
                <span className="admin-card-value">{communicationCounts.open}</span>
                <span className="admin-card-label">Open total</span>
              </button>
            </div>

            <div className="admin-inbox-toolbar">
              <label>
                Status
                <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)}>
                  <option value="open">Open</option>
                  <option value="resolved">Resolved</option>
                  <option value="all">All</option>
                </select>
              </label>
              <label>
                Source
                <select value={sourceFilter} onChange={(event) => setSourceFilter(event.target.value as typeof sourceFilter)}>
                  <option value="all">All sources</option>
                  {Object.entries(COMMUNICATION_SOURCE_LABELS).map(([source, label]) => (
                    <option key={source} value={source}>{label}</option>
                  ))}
                </select>
              </label>
              <button className="admin-btn admin-btn-sm" onClick={loadCommunications} disabled={communicationsLoading}>
                {communicationsLoading ? 'Loading…' : '↻ Refresh'}
              </button>
            </div>

            {communicationsError && <div className="admin-alert admin-alert-error">{communicationsError}</div>}
            {!communicationsLoading && visibleCommunications.length === 0 && (
              <p className="admin-hint">No communication matches these filters.</p>
            )}

            <div className="admin-inbox-list">
              {visibleCommunications.map((item) => {
                const assignedToMe = item.assignee_uid === user.uid;
                return (
                  <article key={item.id} className={`admin-feedback-card admin-inbox-item status-${item.status}`}>
                    <header className="admin-feedback-head">
                      <div>
                        <span className="admin-source-badge">{COMMUNICATION_SOURCE_LABELS[item.source]}</span>
                        <strong>{item.subject}</strong>
                      </div>
                      <span className={`admin-badge ${item.status === 'resolved' ? 'admin-badge-store' : 'admin-badge-env'}`}>
                        {item.status === 'resolved' ? 'Resolved' : item.status === 'in_progress' ? 'In progress' : 'Open'}
                      </span>
                    </header>

                    <p className="admin-hint">
                      {item.sender.name || 'Portal user'}
                      {item.sender.email ? ` · ${item.sender.email}` : ''}
                      {item.sender.phone ? ` · ${item.sender.phone}` : ''}
                      {' · '}
                      {new Date(item.created_at).toLocaleString()}
                      {' · '}
                      <code>{item.source_id}</code>
                    </p>
                    <details className="admin-message-details">
                      <summary>
                        <span className="admin-message-open">Open {item.source === 'book_feedback' ? 'feedback' : 'message'}</span>
                        <span className="admin-message-close">Close {item.source === 'book_feedback' ? 'feedback' : 'message'}</span>
                      </summary>
                      <p className="admin-feedback-body">{item.message || 'No message supplied.'}</p>

                      {Object.keys(item.details).length > 0 && (
                        <dl className="admin-communication-details">
                          {Object.entries(item.details).map(([label, value]) => (
                            <div key={label}>
                              <dt>{label}</dt>
                              <dd>{String(value)}</dd>
                            </div>
                          ))}
                        </dl>
                      )}

                      {item.response && (
                        <div className="admin-existing-response">
                          <strong>Response from {item.response.admin_name}</strong>
                          <p>{item.response.body}</p>
                        </div>
                      )}
                    </details>

                    <div className="admin-assignment-row">
                      <label htmlFor={`assignee-${item.id}`}>Assigned admin</label>
                      <select
                        id={`assignee-${item.id}`}
                        value={item.assignee_uid ?? ''}
                        disabled={updatingCommunicationId === item.id}
                        onChange={(event) => assignCommunication(item, event.target.value)}
                      >
                        <option value="">Unassigned</option>
                        {item.assignee_uid && !communicationAdmins.some((admin) => admin.uid === item.assignee_uid) && (
                          <option value={item.assignee_uid}>{item.assignee_uid} (inactive)</option>
                        )}
                        {communicationAdmins.map((admin) => (
                          <option key={admin.uid} value={admin.uid}>
                            {admin.display_name}{admin.uid === user.uid ? ' (you)' : ''} · {admin.uid}
                          </option>
                        ))}
                      </select>
                      {!assignedToMe && item.status !== 'resolved' && (
                        <button
                          type="button"
                          className="admin-btn admin-btn-ghost admin-btn-sm"
                          disabled={updatingCommunicationId === item.id}
                          onClick={() => assignCommunication(item, user.uid)}
                        >
                          Assign to me
                        </button>
                      )}
                      {item.assignee_uid && <span className="admin-hint">Owner: {adminName(item.assignee_uid)}</span>}
                    </div>

                    {item.status !== 'resolved' && item.source === 'book_feedback' && assignedToMe && (
                      <>
                        <label className="admin-feedback-label" htmlFor={`reply-${item.id}`}>
                          Response the reader will see
                        </label>
                        <textarea
                          id={`reply-${item.id}`}
                          className="admin-textarea"
                          value={replyDrafts[item.id] ?? ''}
                          maxLength={2000}
                          onChange={(event) => setReplyDrafts((prev) => ({ ...prev, [item.id]: event.target.value }))}
                        />
                        <div className="admin-job-actions">
                          <button
                            type="button"
                            className="admin-btn admin-btn-primary admin-btn-sm"
                            disabled={updatingCommunicationId === item.id || !(replyDrafts[item.id] ?? '').trim()}
                            onClick={() => sendReply(item)}
                          >
                            {updatingCommunicationId === item.id ? 'Sending…' : item.response ? 'Update response & resolve' : 'Send response & resolve'}
                          </button>
                        </div>
                      </>
                    )}

                    {item.status !== 'resolved' && assignedToMe && (
                      <div className="admin-job-actions">
                        {item.source !== 'book_feedback' && item.sender.email && (
                          <a className="admin-btn admin-btn-primary admin-btn-sm" href={emailReplyHref(item)}>
                            Reply by email
                          </a>
                        )}
                        <button
                          type="button"
                          className="admin-btn admin-btn-ghost admin-btn-sm"
                          disabled={updatingCommunicationId === item.id}
                          onClick={() => updateCommunicationStatus(item, 'resolved')}
                        >
                          Mark resolved
                        </button>
                      </div>
                    )}

                    {item.status !== 'resolved' && !assignedToMe && (
                      <p className="admin-hint">Assign this item to yourself to respond or mark it resolved.</p>
                    )}

                    {item.status === 'resolved' && (
                      <button
                        type="button"
                        className="admin-btn admin-btn-ghost admin-btn-sm"
                        disabled={updatingCommunicationId === item.id}
                        onClick={() => updateCommunicationStatus(item, 'open')}
                      >
                        Reopen
                      </button>
                    )}
                  </article>
                );
              })}
            </div>
          </>
        )}

        {/* ── Stats tab ── */}
        {activeTab === 'stats' && (
          <>
            <div className="admin-section-actions">
              <button className="admin-btn admin-btn-sm" onClick={loadStats} disabled={statsLoading}>
                {statsLoading ? 'Loading…' : '↻ Refresh'}
              </button>
            </div>

            {statsError && <div className="admin-alert admin-alert-error">{statsError}</div>}

            {stats && (
              <div className="admin-cards">
                <div className="admin-card">
                  <div className="admin-card-icon">👥</div>
                  <div className="admin-card-body">
                    <div className="admin-card-value">{stats.profiles.total}</div>
                    <div className="admin-card-label">Public Profiles</div>
                  </div>
                </div>
                <div className="admin-card">
                  <div className="admin-card-icon">🔑</div>
                  <div className="admin-card-body">
                    <div className="admin-card-value">{stats.admins.total}</div>
                    <div className="admin-card-label">Admin Accounts</div>
                  </div>
                </div>
                {Object.entries(stats.books).map(([fname, counts]) => (
                  <div className="admin-card" key={fname}>
                    <div className="admin-card-icon">{fname.endsWith('.pdf') ? '📄' : '📖'}</div>
                    <div className="admin-card-body">
                      <div className="admin-card-value">{counts.total}</div>
                      <div className="admin-card-label">
                        {fname.endsWith('.pdf') ? 'PDF' : 'EPUB'} Downloads
                      </div>
                      <div className="admin-card-sub">Today: {counts.today}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {stats && (
              <div className="admin-section admin-rate-section">
                <h2>Book Download Rate Limiter</h2>
                <div className="admin-rate-grid">
                  <div className="admin-rate-row">
                    <span>Global today</span>
                    <span className={stats.book_rate_limit.circuit_open ? 'admin-val-danger' : 'admin-val-ok'}>
                      {stats.book_rate_limit.global_window_today} / {stats.book_rate_limit.global_per_day}
                    </span>
                  </div>
                  <div className="admin-rate-row">
                    <span>Circuit breaker</span>
                    <span className={stats.book_rate_limit.circuit_open ? 'admin-val-danger' : 'admin-val-ok'}>
                      {stats.book_rate_limit.circuit_open ? '🔴 OPEN' : '🟢 CLOSED'}
                    </span>
                  </div>
                </div>
                <p className="admin-note">{stats.book_rate_limit._note}</p>
              </div>
            )}

            {stats && stats.reading_analytics && Object.keys(stats.reading_analytics).length > 0 && (
              <div className="admin-section">
                <h2>📖 Reading Analytics (last 7 days)</h2>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Sessions</th>
                      <th>Unique Readers</th>
                      <th>Pages Viewed</th>
                      <th>Avg Pages</th>
                      <th>Avg Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(stats.reading_analytics).map(([date, row]) => (
                      <tr key={date}>
                        <td>{date}</td>
                        <td>{row.sessions}</td>
                        <td>{row.unique_readers}</td>
                        <td>{row.total_pages_viewed}</td>
                        <td>{row.avg_pages_per_session}</td>
                        <td>{row.avg_duration_s}s</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="admin-note">
                  Readers are identified by privacy-preserving UID hashes. Page views are collected per
                  reading session; pages viewed is the unique set of pages opened in a session.
                </p>
              </div>
            )}
          </>
        )}

        {/* ── Admin Users tab ── */}
        {activeTab === 'users' && (
          <>
            {/* Add admin form */}
            <div className="admin-section">
              <h2>Add Admin by UID</h2>
              <p className="admin-hint">
                The Firebase UID to grant is visible on any user's profile page.
              </p>
              <form className="admin-add-form" onSubmit={handleAddAdmin}>
                <input
                  className="admin-input"
                  placeholder="Firebase UID (e.g. abc123XYZdef456)"
                  value={newUid}
                  onChange={(e) => setNewUid(e.target.value)}
                  required
                  autoComplete="off"
                  spellCheck={false}
                />
                <button className="admin-btn admin-btn-primary" type="submit">
                  Add Admin
                </button>
              </form>
              {addError && <div className="admin-alert admin-alert-error">{addError}</div>}
              {addSuccess && <div className="admin-alert admin-alert-success">{addSuccess}</div>}
            </div>

            {/* Admin list */}
            <div className="admin-section">
              <div className="admin-section-head">
                <h2>Current Admins</h2>
                <button className="admin-btn admin-btn-sm" onClick={loadUsers} disabled={usersLoading}>
                  {usersLoading ? 'Loading…' : '↻ Refresh'}
                </button>
              </div>

              {usersError && <div className="admin-alert admin-alert-error">{usersError}</div>}

              {adminUsers && (
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>UID</th>
                      <th>Added by</th>
                      <th>Added at</th>
                      <th>Type</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(adminUsers.admins).map(([uid, meta]) => (
                      <tr key={uid} className={uid === user.uid ? 'admin-row-self' : ''}>
                        <td>
                          <code className="admin-uid">{uid}</code>
                          {uid === user.uid && <span className="admin-self-badge">you</span>}
                        </td>
                        <td><code className="admin-uid-sm">{meta.added_by}</code></td>
                        <td className="admin-date">
                          {meta.added_at ? new Date(meta.added_at).toLocaleDateString() : '—'}
                        </td>
                        <td>
                          {meta.break_glass
                            ? <span className="admin-badge admin-badge-env">env</span>
                            : <span className="admin-badge admin-badge-store">store</span>}
                        </td>
                        <td>
                          {!meta.break_glass && uid !== user.uid && (
                            <button
                              className="admin-btn admin-btn-danger admin-btn-sm"
                              onClick={() => handleRemoveAdmin(uid)}
                              disabled={removing === uid}
                            >
                              {removing === uid ? '…' : 'Remove'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {/* ── Careers tab ── */}
        {activeTab === 'careers' && (
          <>
            <div className="admin-section-actions">
              <button className="admin-btn admin-btn-primary admin-btn-sm" onClick={openNewJob}>
                + Add Job
              </button>
              <button className="admin-btn admin-btn-sm" onClick={loadCareers} disabled={careersLoading}>
                {careersLoading ? 'Loading…' : '↻ Refresh'}
              </button>
            </div>

            {careersError && <div className="admin-alert admin-alert-error">{careersError}</div>}

            {/* ── Job edit / create form ── */}
            {editingJobId !== undefined && (
              <div className="admin-section">
                <div className="admin-section-head">
                  <h2>{editingJobId === null ? 'New Job Posting' : `Edit: ${editingJobId}`}</h2>
                  {/* Locale switcher */}
                  <div className="admin-locale-tabs">
                    {supportedLocales.map(loc => {
                      const jobMeta = careers.find(j => j.id === editingJobId);
                      const hasLocale = jobMeta?.hasLocales.includes(loc);
                      return (
                        <button key={loc}
                          className={`admin-locale-tab ${formLocale === loc ? 'active' : ''}`}
                          onClick={() => handleFormLocaleSwitch(loc)}
                          title={hasLocale ? `Edit ${loc}` : `Add ${loc} translation`}
                        >
                          {loc.toUpperCase()} {hasLocale ? '✓' : '＋'}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <p className="admin-hint">
                  Editing locale: <strong>{formLocale}</strong>.
                  Switch locales above to provide translations. Save is per-locale.
                  Delete removes the job from all locales.
                </p>
                <form className="admin-job-form" onSubmit={handleJobSave}>
                  <div className="admin-job-row">
                    <label>
                      ID (slug)
                      <input className="admin-input" value={jobDraft.id}
                        onChange={e => setJobDraft(d => ({ ...d, id: e.target.value }))}
                        placeholder="auto-derived from title if blank"
                        disabled={editingJobId !== null} />
                    </label>
                    <label>
                      Title
                      <input className="admin-input" required value={jobDraft.title}
                        onChange={e => setJobDraft(d => ({ ...d, title: e.target.value }))} />
                    </label>
                  </div>
                  <div className="admin-job-row">
                    <label>
                      Location
                      <input className="admin-input" value={jobDraft.location}
                        onChange={e => setJobDraft(d => ({ ...d, location: e.target.value }))} />
                    </label>
                    <label>
                      Type
                      <input className="admin-input" value={jobDraft.type}
                        onChange={e => setJobDraft(d => ({ ...d, type: e.target.value }))} />
                    </label>
                    <label>
                      Posted Date
                      <input className="admin-input" type="date" value={jobDraft.postedDate}
                        onChange={e => setJobDraft(d => ({ ...d, postedDate: e.target.value }))} />
                    </label>
                  </div>
                  <label>
                    Description
                    <textarea className="admin-textarea admin-textarea-lg" required value={jobDraft.description}
                      onChange={e => setJobDraft(d => ({ ...d, description: e.target.value }))} />
                  </label>
                  {(['responsibilities', 'qualifications', 'skills', 'benefits'] as const).map(field => (
                    <label key={field}>
                      {field.charAt(0).toUpperCase() + field.slice(1)}{' '}
                      <span className="admin-hint">(one per line)</span>
                      <textarea className="admin-textarea" value={arrToText(jobDraft[field])}
                        onChange={e => setJobDraft(d => ({ ...d, [field]: textToArr(e.target.value) }))} />
                    </label>
                  ))}
                  {jobSaveError && <div className="admin-alert admin-alert-error">{jobSaveError}</div>}
                  <div className="admin-job-actions">
                    <button className="admin-btn admin-btn-primary" type="submit" disabled={jobSaving}>
                      {jobSaving ? 'Saving…' : `Save (${formLocale})`}
                    </button>
                    <button className="admin-btn admin-btn-ghost" type="button"
                      onClick={() => setEditingJobId(undefined)}>
                      Close
                    </button>
                    {editingJobId !== null && (
                      <button className="admin-btn admin-btn-danger admin-btn-sm" type="button"
                        onClick={() => handleJobDelete(editingJobId!)}>
                        🗑 Delete all locales
                      </button>
                    )}
                  </div>
                </form>
              </div>
            )}

            {/* ── Job list ── */}
            <div className="admin-section">
              <h2>Job Postings ({careers.length})</h2>
              {careers.length === 0 && !careersLoading && (
                <p className="admin-hint">No job postings yet. Click "+ Add Job" to create one.</p>
              )}
              {careers.map(job => (
                <div key={job.id} className={`admin-job-card ${editingJobId === job.id ? 'admin-job-card-active' : ''}`}>
                  <div className="admin-job-card-header">
                    <div>
                      <strong>{job.title}</strong>
                      <span className="admin-badge admin-badge-store" style={{ marginLeft: 8 }}>{job.type}</span>
                    </div>
                    <div className="admin-job-card-meta">
                      <span>📍 {job.location}</span>
                      <span>📅 {job.postedDate}</span>
                      {supportedLocales.map(loc => (
                        <span key={loc} className={`admin-locale-badge ${job.hasLocales.includes(loc) ? 'has' : 'missing'}`}>
                          {loc} {job.hasLocales.includes(loc) ? '✓' : '✗'}
                        </span>
                      ))}
                    </div>
                  </div>
                  <p className="admin-hint" style={{ margin: '4px 0 8px' }}>
                    <code className="admin-uid-sm">{job.id}</code>
                    {' · '}
                    {job.description.slice(0, 100)}{job.description.length > 100 ? '…' : ''}
                  </p>
                  <div className="admin-job-card-actions">
                    <button className="admin-btn admin-btn-sm" onClick={() => openEditJob(job)}>✏ Edit</button>
                    <button className="admin-btn admin-btn-danger admin-btn-sm"
                      onClick={() => handleJobDelete(job.id)}>🗑 Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

      </div>
    </div>
  );
}
